import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  fetchGeoConfig,
  loadMapKit,
  MapKit,
  MapLibreMap,
  MapLibreMarker,
  pageCanScroll,
} from './map-provider';
import { GeoConfig, GeoPlace, reportMapFailure, ZoneAtPoint } from '../../../../api/geo-client';
import { GeoAccessContext } from './geo-access';
import { Presence, overlayMotion } from '../../../../motion';
import {
  requestDeviceLocation,
  turnLocationBackOnHelp,
} from '../../../../function-utils/device-location';
import { LocationAsk, LocationAskState } from '../../../location-ask/location-ask';
import {
  AutocompleteSlot,
  FieldError,
  FieldHint,
  InputContainer,
  Label,
  LocationClearButton,
  LocationSummary,
  LocationSummaryLine,
  MapAttribution,
  MapFrame,
  StyledBuildingInput,
  SuggestionItem,
  SuggestionList,
} from './dashboard-buildings.styled';

// Everything a picked place contributes to a building. `address`, `city` and
// `country` are still here because they left the FORM but not the database —
// the reports, the roster and tenant-portal all read them.
export interface BuildingLocation {
  latitude: number | null;
  longitude: number | null;
  // The provider's own id for the place and which provider issued it; stored
  // as a pair or not at all (migration 132).
  place_id: string | null;
  place_provider: string | null;
  formatted_address: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
  country_code: string | null;
}

export const EMPTY_LOCATION: BuildingLocation = {
  latitude: null,
  longitude: null,
  place_id: null,
  place_provider: null,
  formatted_address: null,
  address: null,
  city: null,
  region: null,
  postal_code: null,
  country: null,
  country_code: null,
};

// With NO map at all, hand-typed address text and a pin saved earlier are two
// claims about where the building is, and the text is the one the user just
// changed. The pair is dropped TOGETHER — a latitude without a longitude is not
// a location — so an edited address can never sit next to coordinates pointing
// somewhere the building is not. Only the no-map fallback uses this: when the
// map is on screen the person can see the pin and move it themselves.
const clearedPin: Pick<BuildingLocation, 'latitude' | 'longitude'> = {
  latitude: null,
  longitude: null,
};

interface Point {
  lat: number;
  lng: number;
}

interface Props {
  value: BuildingLocation;
  onChange: (next: BuildingLocation) => void;
  invalid?: boolean;
  /** The surrounding form is read-only: the pin cannot be moved. */
  disabled?: boolean;
  /**
   * TRUE while the box holds typed text that has not resolved to a pin.
   *
   * The parent cannot work this out for itself: the typed text lives in this
   * component's local `query`, and the parent only ever sees `value`, which is
   * still EMPTY_LOCATION at that moment. Without this the forms could not tell
   * "no address was entered" (fine — location is optional) apart from "an
   * address was entered and thrown away" (a silent data loss), so they let the
   * second one through.
   */
  onUnresolvedChange?: (unresolved: boolean) => void;
  /**
   * The time zone under the pin, each time the pin moves; null when the pin is
   * cleared. The server derives the zone again on save — this is so the form
   * can SHOW it before Save, and ask when a pin has no single zone.
   */
  onZoneHint?: (hint: ZoneAtPoint | null) => void;
  /**
   * Where to open the map for a building with no pin yet — another building of
   * the same organization is a far better guess than the whole world.
   */
  nearby?: Point | null;
  /**
   * Use a location permission granted EARLIER, without a tap, to centre the
   * map. Off on the signup wizard, where a location is only ever taken after
   * somebody asks for it.
   */
  useLocationWhenGranted?: boolean;
  /**
   * The typed Address / City / Country fields. Off on the signup wizard, which
   * has its own City field: there, with no map the picker shows nothing, and
   * with a map but no address search it shows the map alone.
   */
  manualFields?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;
const MIN_SEARCH_CHARS = 3;
// The style and its first tiles must arrive within this, or the map counts as
// down. Without a deadline a blocked tile host leaves an empty grey box.
const FIRST_PAINT_TIMEOUT_MS = 10000;
const PIN_ZOOM = 16;
const AREA_ZOOM = 12;
// The whole world, for a building with no pin and nothing better to go on.
const WORLD = { center: [10, 25] as [number, number], zoom: 1.2 };

const toLocation = (place: GeoPlace, lat: number, lng: number): BuildingLocation => ({
  latitude: lat,
  longitude: lng,
  // A pair or nothing: an id means nothing without the provider that issued it.
  place_id: place.place_id && place.place_provider ? place.place_id : null,
  place_provider: place.place_id && place.place_provider ? place.place_provider : null,
  formatted_address: place.formatted_address || place.label,
  address: place.address || place.label,
  city: place.city,
  region: place.region,
  postal_code: place.postal_code,
  country: place.country,
  // ISO-3166 alpha-2. chk_properties_country_code rejects anything else.
  country_code:
    place.country_code && /^[A-Za-z]{2}$/.test(place.country_code)
      ? place.country_code.toUpperCase()
      : null,
});

type LocateState = 'idle' | 'asking' | 'blocked' | 'failed';

export const BuildingLocationPicker: React.FC<Props> = ({
  value,
  onChange,
  invalid,
  disabled,
  onUnresolvedChange,
  onZoneHint,
  nearby,
  useLocationWhenGranted = true,
  manualFields: withManualFields = true,
}) => {
  const [kit, setKit] = useState<MapKit | null>(null);
  const [config, setConfig] = useState<GeoConfig | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [query, setQuery] = useState(value.formatted_address || '');
  const [suggestions, setSuggestions] = useState<GeoPlace[]>([]);
  // The last search came back empty because the lookup FAILED, not because
  // nothing matched. The two look the same in the list; they are said apart.
  const [searchFailed, setSearchFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [locate, setLocate] = useState<LocateState>('idle');
  // The pin was put where this device is ("Use my location"). Said under the
  // map until the pin is moved by hand: where the person stands is often near
  // the building and not on it.
  const [placedFromDevice, setPlacedFromDevice] = useState(false);
  // Staff routes, or the signed-out ones on the signup wizard (geo-access.tsx).
  const geoAccess = useContext(GeoAccessContext);
  const geoAccessRef = useRef(geoAccess);
  geoAccessRef.current = geoAccess;
  // Where our own ask stands. It carries "Use my location" itself, so the
  // standing button under the map waits until the ask is KNOWN to be out of the
  // way — not merely not-yet-shown, which drew both for a frame.
  const [askState, setAskState] = useState<LocationAskState>('checking');

  // Address search needs the provider; the map does not. With the map up and
  // no provider the person types the address and taps the map for the pin.
  const searchOn = status === 'ready' && Boolean(config && config.geocoding);
  const hasPin = value.latitude !== null && value.longitude !== null;

  // Report "typed but not pinned" upward so the forms can refuse the save.
  //
  // THREE things matter. Text present, AND no coordinate, AND a search that
  // could actually have produced one. Without address search there is nothing
  // to resolve against, so a hand-typed address IS the complete answer and a
  // null latitude is the expected state, not an error — gating on `searchOn`
  // is what stops this from condemning every save on the branch every user is
  // on while no provider is configured. An empty box is not an error either —
  // location is optional on a building.
  //
  // "Not pinned" is not the whole of it. On a building that ALREADY has a pin,
  // retyping the box (or emptying it) and pressing Save used to report
  // "Building updated" while keeping the old address and the old pin — the new
  // text was simply dropped. So the test is whether the box still says what
  // the saved location says, not whether a pin exists.
  const savedText = (value.formatted_address || value.address || '').trim();
  const unresolved =
    searchOn && (hasPin ? query.trim() !== savedText : query.trim().length > 0);

  useEffect(() => {
    onUnresolvedChange?.(unresolved);
  }, [unresolved, onUnresolvedChange]);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapObj = useRef<MapLibreMap | null>(null);
  const markerObj = useRef<MapLibreMarker | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  // The user has moved the map or the pin: nothing automatic may move it again.
  const touched = useRef(false);
  // Re-decides the map's two-finger rule (set where the map is created).
  const syncGesturesRef = useRef<(() => void) | null>(null);
  // After EVERY render: what this component draws under the map (the pin's
  // summary, the address fields, a hint) is what most often changes whether
  // the page can scroll. No dependency list, on purpose; the check is cheap.
  useEffect(() => {
    syncGesturesRef.current?.();
  });
  // WHICH PIN A LOOKUP BELONGS TO. Every move, pick and clear takes the next
  // number; an address or zone answer is used only if its number is still the
  // current one. Without this a slow answer for pin A landed after the person
  // had moved to B (or pressed Clear) and put the pin, the address and the zone
  // back to A — under the cursor, if they were mid-drag.
  const pinSeq = useRef(0);
  // Parent callbacks and the latest value change identity every render; refs
  // keep the map's own listeners from being torn down and rebuilt with them.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onZoneHintRef = useRef(onZoneHint);
  onZoneHintRef.current = onZoneHint;
  const valueRef = useRef(value);
  valueRef.current = value;
  const searchOnRef = useRef(searchOn);
  searchOnRef.current = searchOn;
  const disabledRef = useRef(Boolean(disabled));
  disabledRef.current = Boolean(disabled);

  // Keep the visible text in step when the parent loads a building.
  //
  // This TRACKS the incoming value. Typing in the search box only moves the
  // LOCAL `query`; these deps change solely via choose() / a moved pin /
  // clear(), each of which already sets the same text itself.
  useEffect(() => {
    setQuery(value.formatted_address || value.address || '');
  }, [value.formatted_address, value.address]);

  /* ── Load the configuration and the renderer ── */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const loadedConfig = await fetchGeoConfig();
        if (!alive) return;
        setConfig(loadedConfig);
        const loadedKit = await loadMapKit(loadedConfig);
        if (!alive) return;
        setKit(loadedKit);
        setStatus('ready');
      } catch (error) {
        if (alive) setStatus('unavailable');
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const reportZone = useCallback(async (lat: number, lng: number, seq: number) => {
    const hint = await geoAccessRef.current.zoneAt(lat, lng);
    if (seq !== pinSeq.current) return;
    // A failed request must not leave the PREVIOUS pin's zone on screen as
    // though it belonged to this one: say "nothing known", and the field falls
    // back. The server derives the zone again on save either way.
    onZoneHintRef.current?.(hint);
  }, []);

  // Filled in below, once movePin exists; the marker's own listener reads it
  // late, so it always calls the current one.
  const movePinRef = useRef<(lat: number, lng: number) => void>(() => undefined);

  const showMarker = useCallback(
    (lat: number, lng: number) => {
      if (!kit || !mapObj.current) return;

      if (!markerObj.current) {
        const marker = new kit.Marker({ draggable: !disabledRef.current })
          .setLngLat([lng, lat])
          .addTo(mapObj.current);
        marker.on('dragend', () => {
          // .wrap(): zoomed far out the world repeats, and a pin dropped on a
          // copy reports a longitude like 190 — which every lookup and the
          // save itself reject.
          const pos = marker.getLngLat().wrap();
          movePinRef.current(pos.lat, pos.lng);
        });
        markerObj.current = marker;
      } else {
        markerObj.current.setLngLat([lng, lat]);
      }
    },
    [kit]
  );

  // The pin was put somewhere BY HAND (dragged, or the map was tapped).
  const movePin = useCallback(
    async (lat: number, lng: number) => {
      touched.current = true;
      setPlacedFromDevice(false);
      pinSeq.current += 1;
      const seq = pinSeq.current;
      showMarker(lat, lng);
      reportZone(lat, lng, seq);

      // THE PIN IS SAVED THE MOMENT IT MOVES, with the address that was there.
      // It used to be written only when the address lookup came back, so a
      // Save pressed in that window posted the old pin while the map showed
      // the new one. The place id goes: it named the old spot.
      const moved: BuildingLocation = {
        ...valueRef.current,
        latitude: lat,
        longitude: lng,
        place_id: null,
        place_provider: null,
      };
      onChangeRef.current(moved);

      // No address lookup: whatever was typed stays, the pin is the new fact.
      if (!searchOnRef.current) return;

      const place = await geoAccessRef.current.reverse(lat, lng);
      if (seq !== pinSeq.current) return;
      // Only a lookup that ANSWERED replaces the address. "Nothing there" and
      // "the provider is down" both come back empty, and wiping a saved
      // address, city and country because a lookup failed while somebody
      // nudged the pin ten metres is the worse of the two mistakes — the
      // reports read those columns.
      if (!place) return;
      const next = toLocation(place, lat, lng);
      setQuery(next.formatted_address || '');
      onChangeRef.current(next);
    },
    [showMarker, reportZone]
  );
  movePinRef.current = movePin;

  /* ── Map + draggable marker ── */
  useEffect(() => {
    if (!kit || !mapRef.current || mapObj.current) return undefined;

    const start = valueRef.current;
    const pinned = start.latitude !== null && start.longitude !== null;
    let map: MapLibreMap;

    try {
      map = new kit.Map({
        container: mapRef.current,
        style: kit.styleUrl,
        center: pinned
          ? [start.longitude as number, start.latitude as number]
          : nearby
            ? [nearby.lng, nearby.lat]
            : WORLD.center,
        zoom: pinned ? PIN_ZOOM : nearby ? AREA_ZOOM : WORLD.zoom,
        // This map sits inside a scrolling form. Without this, a finger or a
        // wheel moving down the page is swallowed by the map and the form
        // stops scrolling: two fingers pan, Ctrl + wheel zooms.
        cooperativeGestures: true,
      });
    } catch (error) {
      // No WebGL after all, or the container could not be drawn into.
      setStatus('unavailable');

      return undefined;
    }

    mapObj.current = map;
    map.addControl(new kit.NavigationControl({ showCompass: false }), 'top-right');

    // COOPERATIVE GESTURES ONLY WHERE THERE IS A SCROLL TO PROTECT. They are
    // created on (above), because a form is usually longer than the screen. On
    // a page that cannot scroll — an installed app on a tall screen — the map's
    // own hint would tell somebody to "use two fingers" for nothing, so they
    // are switched off there, and back on if the page grows (the pin's summary
    // and the address fields appear below the map as the form is filled in).
    const gestures = (
      map as unknown as {
        cooperativeGestures?: { enable: () => void; disable: () => void };
      }
    ).cooperativeGestures;
    const syncGestures = () => {
      if (!gestures) return;
      if (pageCanScroll(mapRef.current)) gestures.enable();
      else gestures.disable();
    };
    syncGesturesRef.current = syncGestures;
    // NOT decided at creation: the page is still being laid out then, and a
    // page that is short for a moment would lose the rule for good. They stay
    // ON (the safe default — a map must never swallow a form's scroll) until
    // the map has settled, and are re-decided whenever the page can have
    // changed height: the map going idle, the window or the document resizing,
    // and every render of this component (see the effect below). Observing
    // document.body alone missed the common case — a body pinned to the
    // viewport's height does not resize when its content outgrows it.
    map.on('idle', syncGestures);
    window.addEventListener('resize', syncGestures);
    const grown =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGestures) : null;
    grown?.observe(document.documentElement);
    grown?.observe(document.body);
    if (mapRef.current?.parentElement) grown?.observe(mapRef.current.parentElement);

    // The map counts as working once its style and first tiles have arrived.
    // Before that, any error or the deadline drops to typed address fields;
    // after it, a single failed tile is not a reason to take the map away.
    let painted = false;
    const giveUp = () => {
      if (painted) return;
      setStatus('unavailable');
      // Seen only by this browser: tell the Super Admin console once.
      reportMapFailure({ kind: 'style', message: 'the map did not draw within 10 seconds' });
    };
    const deadline = setTimeout(giveUp, FIRST_PAINT_TIMEOUT_MS);
    // 'style.load', not 'load': 'load' waits for every visible tile, and on a
    // slow connection or in a background tab that can take longer than the
    // deadline — the map was then taken away from somebody who could see it
    // and had already placed a pin. The style arriving proves the tile host
    // answers, which is what the deadline is there to detect.
    map.on('style.load', () => {
      painted = true;
      clearTimeout(deadline);
    });
    // A style-level failure, not one missing tile: tile and source errors carry
    // the source they came from.
    map.on('error', event => {
      const scoped = event as unknown as {
        sourceId?: string;
        tile?: unknown;
        error?: { status?: number; message?: string; url?: string };
      };
      // What the browser met, for the console's Map page (once per page):
      // MapLibre's own message and status, never where the map was looking.
      reportMapFailure({
        kind: scoped.tile ? 'tile' : scoped.sourceId ? 'source' : 'style',
        status: scoped.error?.status ?? null,
        message: scoped.error?.message ?? null,
        url: scoped.error?.url ?? null,
      });
      if (!scoped.sourceId && !scoped.tile) giveUp();
    });

    map.on('dragstart', () => {
      touched.current = true;
    });
    map.on('click', event => {
      if (disabledRef.current) return;
      // A click ON the pin bubbles here too. The pin's tip is not where the
      // pointer is, so treating it as "put the pin here" made the pin hop a
      // few pixels every time it was clicked.
      const target = event.originalEvent.target as Element | null;
      if (target && target.closest && target.closest('.maplibregl-marker')) return;
      const at = event.lngLat.wrap();
      movePinRef.current(at.lat, at.lng);
    });

    if (pinned) {
      showMarker(start.latitude as number, start.longitude as number);
    }

    return () => {
      clearTimeout(deadline);
      window.removeEventListener('resize', syncGestures);
      grown?.disconnect();
      syncGesturesRef.current = null;
      map.remove();
      mapObj.current = null;
      markerObj.current = null;
    };
    // eslint-disable-next-line
  }, [kit, status]);

  // The parent loaded a different building, or cleared the location: follow it.
  useEffect(() => {
    if (!mapObj.current) return;

    if (value.latitude === null || value.longitude === null) {
      if (markerObj.current) {
        markerObj.current.remove();
        markerObj.current = null;
      }

      return;
    }

    const at = markerObj.current ? markerObj.current.getLngLat() : null;
    if (at && at.lat === value.latitude && at.lng === value.longitude) return;

    showMarker(value.latitude, value.longitude);
    mapObj.current.jumpTo({
      center: [value.longitude, value.latitude],
      zoom: Math.max(mapObj.current.getZoom(), PIN_ZOOM),
    });
  }, [value.latitude, value.longitude, showMarker, kit]);

  useEffect(() => {
    if (markerObj.current) markerObj.current.setDraggable(!disabled);
  }, [disabled]);

  /* ── "Use my location" ──
     A TAP puts the pin where this device is — address and time zone looked up
     from it — and the pin stays draggable, so the person moves it onto the
     building if they are not standing at it (owner, 2026-10-14: "use current
     location and then adjust the map"; this replaces R-T2's "only ever
     centres"). A position that merely ARRIVES — permission granted earlier,
     nobody tapped anything — still only centres the map: nothing becomes the
     building's location without somebody asking for it. */
  const centreAt = useCallback((latitude: number, longitude: number, silent: boolean) => {
    // A late fix must never move a map the person has already moved.
    if (silent && touched.current) return;
    if (!mapObj.current) return;
    mapObj.current.flyTo({ center: [longitude, latitude], zoom: AREA_ZOOM });
  }, []);

  const placeAtMe = useCallback((latitude: number, longitude: number) => {
    if (!mapObj.current || disabledRef.current) return;
    mapObj.current.flyTo({ center: [longitude, latitude], zoom: PIN_ZOOM });
    void movePinRef.current(latitude, longitude);
    setPlacedFromDevice(true);
  }, []);

  /* The button under the map. The asking, and the ONE retry for anything but a
     refusal, live in function-utils/device-location (requestDeviceLocation) —
     this used to be a private copy of them. */
  const centreOnMe = useCallback(async () => {
    setLocate('asking');
    const outcome = await requestDeviceLocation();
    if (outcome.ok) {
      setLocate('idle');
      placeAtMe(outcome.latitude, outcome.longitude);

      return;
    }
    setLocate(outcome.reason === 'denied' ? 'blocked' : 'failed');
  }, [placeAtMe]);

  /* THE ASK (R-T10, WP-C11). A building with no pin and no nearby building to
     open on gets OUR ask above the map — what the location is for, "Use my
     location", "Not now" — and the browser's prompt only after the tap. With
     the permission already granted there is no ask: <LocationAsk> uses it
     silently and the map is centred, unless the person has touched it first.
     (That silent path used to be an effect of this component's own.) */
  const askForLocation =
    status === 'ready' && Boolean(kit) && !disabled && !hasPin && !nearby;

  /* ── Address search ── */
  useEffect(() => {
    if (!searchOn || query.trim().length < MIN_SEARCH_CHARS || !open) {
      setSuggestions([]);
      // A closed list keeps what the last search said; only text too short to
      // search (or no search at all) has nothing left to say it about.
      if (!searchOn || query.trim().length < MIN_SEARCH_CHARS) setSearchFailed(false);

      return undefined;
    }

    let alive = true;
    const timer = setTimeout(async () => {
      const centre = mapObj.current ? mapObj.current.getCenter() : null;
      // Bias towards what the map is showing — but not while it still shows the
      // whole world, where the centre is the middle of nowhere in particular.
      const near =
        centre && mapObj.current && mapObj.current.getZoom() > 4
          ? { lat: centre.lat, lng: centre.lng }
          : null;
      let failed = false;
      const found = await geoAccessRef.current.search(query.trim(), near, () => {
        failed = true;
      });
      // A slow answer to an old question must not replace a newer one.
      if (alive) {
        setSuggestions(found);
        setSearchFailed(failed);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, searchOn, open]);

  const choose = (place: GeoPlace) => {
    setOpen(false);
    setSuggestions([]);
    touched.current = true;
    pinSeq.current += 1;

    const next = toLocation(place, place.latitude, place.longitude);
    setQuery(next.formatted_address || '');
    onChange(next);
    showMarker(place.latitude, place.longitude);
    if (mapObj.current) {
      mapObj.current.flyTo({
        center: [place.longitude, place.latitude],
        zoom: PIN_ZOOM,
      });
    }
    reportZone(place.latitude, place.longitude, pinSeq.current);
    // Back to the box the list hung from, not to <body>.
    searchRef.current?.focus();
  };

  const clear = () => {
    // Anything still in flight belongs to the pin that is being removed.
    pinSeq.current += 1;
    setQuery('');
    setSuggestions([]);
    onChange({ ...EMPTY_LOCATION });
    onZoneHint?.(null);
    if (markerObj.current) {
      markerObj.current.remove();
      markerObj.current = null;
    }
  };

  // Close the suggestion list when the pointer OR the keyboard leaves it. It
  // used to close on an outside click only, so a keyboard user who tabbed on
  // left the list hanging open over the fields below.
  useEffect(() => {
    if (!open) return undefined;

    const leaving = (event: Event) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', leaving);
    document.addEventListener('focusin', leaving);

    return () => {
      document.removeEventListener('mousedown', leaving);
      document.removeEventListener('focusin', leaving);
    };
  }, [open]);

  const listShown = open && suggestions.length > 0;
  const focusOption = (index: number) => {
    const option = boxRef.current?.querySelector<HTMLElement>(
      `[data-suggestion="${index}"]`
    );
    option?.focus();
  };

  // The three typed fields: the whole picker when there is no map, and the
  // address half of it when there is a map but no address search.
  const manualFields = (keepPin: boolean) => (
    <>
      <InputContainer>
        <Label htmlFor="building-address">Address</Label>
        <StyledBuildingInput
          id="building-address"
          type="text"
          value={value.address || ''}
          placeholder="Enter property location"
          onChange={e =>
            onChange({
              ...value,
              address: e.target.value,
              // Typed text replaces whatever a lookup wrote for display.
              formatted_address: null,
              ...(keepPin ? {} : clearedPin),
            })
          }
        />
      </InputContainer>
      <InputContainer>
        <Label htmlFor="building-city">City</Label>
        <StyledBuildingInput
          id="building-city"
          type="text"
          value={value.city || ''}
          placeholder="Enter city"
          onChange={e =>
            onChange({
              ...value,
              city: e.target.value,
              ...(keepPin ? {} : clearedPin),
            })
          }
        />
      </InputContainer>
      {/* A plain text box, not an ISO select. country_code is the one location
          column with a CHECK, and hand-editing the country NAME says nothing
          about the ISO-3166 code already stored — so it is left as it was. */}
      <InputContainer>
        <Label htmlFor="building-country">Country</Label>
        <StyledBuildingInput
          id="building-country"
          type="text"
          value={value.country || ''}
          placeholder="Enter country"
          onChange={e => onChange({ ...value, country: e.target.value })}
        />
      </InputContainer>
    </>
  );

  /* ── Fallback: no map ──
     Adding a building must never become impossible because a tile host is
     down, the browser cannot draw the map, or the map is switched off. */
  if (status === 'unavailable') {
    if (!withManualFields) return null;

    return (
      <>
        {manualFields(false)}
        <FieldHint>
          The map is unavailable right now — enter the address by hand.
        </FieldHint>
        <FieldError show={Boolean(invalid)}>
          Please enter the building&apos;s location*
        </FieldError>
      </>
    );
  }

  const mapBlock = (
    <>
      {askForLocation ? (
        <LocationAsk
          purpose="Share your location and the pin is placed where you are. If you are not at the building, drag the pin onto it."
          // A tap is an instruction: the pin goes there. A permission that was
          // merely already granted only centres the map, and never one the
          // person has touched.
          onLocated={(position, how) =>
            how.asked
              ? placeAtMe(position.latitude, position.longitude)
              : centreAt(position.latitude, position.longitude, true)
          }
          onState={setAskState}
          useWhenGranted={useLocationWhenGranted}
        />
      ) : null}
      <MapFrame ref={mapRef} role="group" aria-label="Building location map" />

      {/* The standing control. Hidden while our ask is on screen: the ask has
          the same button, and two of them is one too many. */}
      {!disabled && (!askForLocation || askState === 'hidden') ? (
        <LocationClearButton
          type="button"
          onClick={() => void centreOnMe()}
          disabled={status !== 'ready' || locate === 'asking'}
        >
          {locate === 'asking' ? 'Finding your location…' : 'Use my location'}
        </LocationClearButton>
      ) : null}
      {locate === 'blocked' ? (
        <FieldHint>
          Location is switched off for Lobbero on this device. Search for the address or
          move the map instead. {turnLocationBackOnHelp()}
        </FieldHint>
      ) : null}
      {locate === 'failed' ? (
        <FieldHint>
          Your location could not be found. Search for the address or move the map
          instead.
        </FieldHint>
      ) : null}
      {placedFromDevice && hasPin ? (
        <FieldHint data-pin-from-device>
          The pin is where you are now. If you are not at the building, drag the pin onto
          it or tap the building on the map.
        </FieldHint>
      ) : null}

      {hasPin ? (
        <LocationSummary>
          <LocationSummaryLine>
            {value.formatted_address || value.address || 'Pinned location'}
          </LocationSummaryLine>
          <LocationSummaryLine muted>
            {(value.latitude as number).toFixed(5)},{' '}
            {(value.longitude as number).toFixed(5)}
            {value.city ? ` · ${value.city}` : ''}
            {value.country ? `, ${value.country}` : ''}
          </LocationSummaryLine>
          {!disabled ? (
            <LocationClearButton type="button" onClick={clear}>
              Clear location
            </LocationClearButton>
          ) : null}
        </LocationSummary>
      ) : (
        <FieldHint>
          {searchOn
            ? 'Search for the address above, or tap the building on the map. Drag the pin to fine-tune it.'
            : 'Tap the building on the map to drop a pin — it sets the building’s time zone.'}
        </FieldHint>
      )}
    </>
  );

  // The map is up but there is no address search: type the address, tap the pin.
  if (status === 'ready' && !searchOn) {
    return (
      <>
        {withManualFields ? manualFields(true) : null}
        <InputContainer>
          <Label as="p">Location on the map</Label>
          {mapBlock}
          <FieldError show={Boolean(invalid)}>
            Please enter the building&apos;s location*
          </FieldError>
        </InputContainer>
      </>
    );
  }

  return (
    <InputContainer>
      <Label htmlFor="building-location">Location</Label>

      <AutocompleteSlot ref={boxRef}>
        <StyledBuildingInput
          ref={searchRef}
          id="building-location"
          type="text"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={listShown}
          aria-controls="building-location-suggestions"
          value={query}
          placeholder={
            status === 'loading'
              ? 'Loading map…'
              : 'Start typing an address, then pick a match'
          }
          disabled={status === 'loading'}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            // Escape is taken only while there IS a list to close; otherwise it
            // belongs to whatever holds this field (the edit modal closes on it).
            if (e.key === 'Escape' && listShown) {
              e.stopPropagation();
              setOpen(false);
            }
            if (e.key === 'ArrowDown' && listShown) {
              e.preventDefault();
              focusOption(0);
            }
          }}
        />

        {/* The list grows down from the input and shrinks back when it
            closes: overlay kind 'popover' inside a Presence, origin at the
            top-left corner it hangs from. */}
        <Presence>
          {listShown ? (
            <SuggestionList
              key="suggestions"
              id="building-location-suggestions"
              {...overlayMotion('popover')}
              style={{ transformOrigin: 'top left' }}
              role="listbox"
              aria-label="Matching addresses"
            >
              {suggestions.map((place, index) => (
                <SuggestionItem
                  key={place.place_id || `${place.label}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  data-suggestion={index}
                  onClick={() => choose(place)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      focusOption(Math.min(index + 1, suggestions.length - 1));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      if (index === 0) searchRef.current?.focus();
                      else focusOption(index - 1);
                    } else if (e.key === 'Escape') {
                      // Close the LIST, not the modal the form sits in.
                      e.stopPropagation();
                      setOpen(false);
                      searchRef.current?.focus();
                    }
                  }}
                >
                  {place.label || place.formatted_address || ''}
                </SuggestionItem>
              ))}
            </SuggestionList>
          ) : null}
        </Presence>
      </AutocompleteSlot>
      {config && config.geocoding ? (
        <MapAttribution>
          <a
            href={config.geocoding.attribution_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {config.geocoding.attribution}
          </a>
        </MapAttribution>
      ) : null}

      {mapBlock}

      {/* TYPED, BUT NOT CHOSEN — the state in which this field silently threw
          the address away. `query` is local; the ONLY things that write the
          parent's location are choose(), a moved pin and clear(). So typing an
          address and pressing Save posted EMPTY_LOCATION while the address the
          user typed was still visibly sitting in the box, and the building was
          saved with no address, no city and no coordinates. */}
      <FieldError show={unresolved && !searchFailed}>
        Pick a match from the list — this address is not saved until you do.
      </FieldError>

      {/* …and when there IS no list because the search itself failed, "pick a
          match" asks for something the person cannot do. Say what happened and
          the two ways on. */}
      <FieldError show={unresolved && searchFailed}>
        Address search is not answering right now. Try again in a moment, or place the pin
        on the map.
      </FieldError>

      <FieldError show={Boolean(invalid)}>
        Please choose the building&apos;s location*
      </FieldError>
    </InputContainer>
  );
};
